import React, { useState, useEffect } from 'react'
import { useHistory, Redirect } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { Form, Input, message } from 'antd'
import { Mail, Lock, ArrowRight, Eye, EyeOff, Car, MapPin } from 'lucide-react'
import { AUTH } from '../../utils/apis'
import { authToken, loginUser } from '../../Redux/actions/authActions'
import './LoginPage.css'
import paths from '../../Config/paths'
import { secureApi } from '../../Config/api'
import { getFcmToken } from '../../Config/firebase'

const Login = () => {
  const user = useSelector(state => state.authReducer.user)
  const dispatch = useDispatch()
  const history = useHistory()
  const [form] = Form.useForm() 

  const [passwordVisible, setPasswordVisible] = useState(false)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    form.setFieldsValue({ email, password })
  }, [email, password, form])

  if (user) {
    return <Redirect to={paths?.HOME} />
  }

  const handleLogin = async () => {
    try {
      setLoading(true)
      setError('')

      if (!email || !password) {
        message.error('Please provide all fields')
        setLoading(false)
        return
      }

      const fcmToken = await getFcmToken()
      const response = await secureApi?.post(AUTH?.LOGIN, { email, password, fcmToken })

      if (response?.success !== false) {
        const token = response?.encodedToken
        const userData = response?.user
        dispatch(loginUser(userData, token))
        dispatch(authToken(token))
        history.push(paths?.HOME)
      } else {
        message.error(response?.message || 'Invalid credentials')
        setPassword('')
        form.setFieldsValue({ password: '' }) 
      }
    } catch (err) {
      const errorMessage =
        err.response?.data?.message || 'Something went wrong. Please try again.'
      setError(errorMessage)
      message.error(errorMessage)
      setPassword('')
      form.setFieldsValue({ password: '' })
    } finally {
      setLoading(false)
    }
  }

  const forgotPassword = () => {
    history.push(paths?.FORGOT_PASSWORD)
  }

  const handleKeyPress = (e) => {
    if (e?.key === 'Enter') {
      handleLogin()
    }
  }

  const togglePasswordVisibility = () => {
    setPasswordVisible(!passwordVisible)
  }

  return (
    <div className="login-container">
      <div className="left-section">
        <div className="left-content">
          <div className="logo-section">
            <div className="logo-icon">
              <span className="logo-letter">B</span>
            </div>
            <div className="logo-text">
              <div className="company-name">Boat</div>
              <div className="company-subtitle">Track</div>
            </div>
          </div>

          <div className="welcome-section">
            <h1 className="main-heading">
              Welcome to <br />
              <span className="dashboard-text">Boat Track Dashboard</span>
            </h1>
            <p className="description">
              Track your vehicles in real-time and create smart
              geofencing alerts. Get instant email notifications
              when a vehicle leaves your defined radius.
            </p>
          </div>
          <div className="features-section">
            <div className="feature-card">
              <div className="feature-icon analytics-icon">
                <Car size={24} />
              </div>
              <div className="feature-content">
                <h3 className="feature-title">Vehicle Tracking</h3>
                <p className="feature-description">
                  Monitor your vehicles live on the map
                </p>
              </div>
            </div>

            <div className="feature-card">
              <div className="feature-icon security-icon">
                <MapPin size={24} />
              </div>
              <div className="feature-content">
                <h3 className="feature-title">Geofence Alerts</h3>
                <p className="feature-description">
                  Get email alerts when a vehicle exits your radius
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="right-section">
        <div className="login-form-container">
          <div className="form-header">
            <h2 className="form-title">Welcome back</h2>
            <p className="form-subtitle">Sign in to access your Boat Track Dashboard</p>
          </div>

          <Form 
            form={form} 
            layout="vertical" 
            className="login-form" 
            onKeyDown={handleKeyPress}
          >
            <Form.Item
              label="Email"
              name="email"
              rules={[{ required: true, message: 'Please enter your Email' }]}
              className="input-group"
            >
              <div className="input-wrapper">
                <Mail className="input-icon" size={20} />
                <Input
                  type="email"
                  placeholder="Enter Your Email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="form-input"
                />
              </div>
            </Form.Item>

            <Form.Item
              name="password"
              label="Password"
              rules={[
                { required: true, message: 'Please Enter Your Password' },
                { min: 8, message: 'Password must be at least 8 characters' }
              ]}
              className="input-group"
            >
              <div className="input-wrapper">
                <Lock className="input-icon" size={20} />
                <Input
                  type={passwordVisible ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="form-input"
                />
                <button
                  type="button"
                  onClick={togglePasswordVisibility}
                  className="password-toggle"
                >
                  {passwordVisible ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </Form.Item>

            <Form.Item>
              <button
                type="button"
                onClick={handleLogin}
                disabled={loading}
                className={`submit-button ${loading ? 'loading' : ''}`}
              >
                {loading ? 'Signing in...' : (
                  <>
                    Sign in
                    <ArrowRight size={20} />
                  </>
                )}
              </button>
            </Form.Item>
          </Form>

          <div className="remember-me">
            <p className="forgot-password-text" onClick={forgotPassword}>
              Forgot password?
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Login